import { useEffect, useState } from "react";

/**
 * CinematicImage — responsive focal-point–aware background image.
 *
 * Solves the "subjects get cropped on mobile" problem by letting each
 * surface declare separate focal points for mobile vs desktop, instead
 * of relying on `bg-center` which cuts off heads on tall portraits and
 * loses architecture on ultra-wide.
 *
 * Examples:
 *   <CinematicImage src={IMG.dance1} mobile="50% 25%" desktop="50% 38%" />
 *   <CinematicImage src={IMG.heroPortal} mobile="55% 35%" desktop="center" />
 *
 * The default mobile focal point lifts to 30% (faces / silhouettes stay
 * in frame) and desktop holds centre.
 */
export default function CinematicImage({
  src,
  alt = "",
  mobile = "50% 30%",
  desktop = "center",
  className = "",
  testId,
  scale = 1.02,
}) {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  return (
    <div
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      data-testid={testId}
      className={`absolute inset-0 bg-cover bg-no-repeat ${className}`}
      style={{
        backgroundImage: `url(${src})`,
        backgroundPosition: isDesktop ? desktop : mobile,
        transform: `scale(${scale})`,
      }}
    />
  );
}
